// Checker side of a ROW-grain duplicate verdict.
//
// Stage 64 moved the Raw Data verdict to row grain and set its status to
// `resolved_pending_review` on any client whose policy has a checker on
// duplicate verdicts — which is the DEFAULT. Nothing then listed, approved or
// rejected one: the review queue reads `Resolution` rows, and a row verdict is
// not one. Every verdict on the live engagement landed in a state with no exit,
// while the block-policy gate counted it as finished work.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api, postJson } from './client'
import type { RawDupPending } from './types'

export function useRawDupReview(clientId: string) {
  return useQuery({
    queryKey: ['raw-dup-review', clientId],
    queryFn: () => api<RawDupPending[]>(`/clients/${clientId}/raw-duplicate-review`),
    enabled: Boolean(clientId),
  })
}

/** One file's worth of pending verdicts, and how many THIS checker can pass.
 *
 *  `POST /uploads/{id}/duplicates/review` judges a file's pending verdicts
 *  together, skipping any the caller recorded — two people look at everything,
 *  and a bulk door that let one person satisfy both halves would be the thing
 *  the policy exists to prevent. So the button's promise is `theirs`, not
 *  `total`: a label saying 233 when 10 are the checker's own would approve 223
 *  and read as broken. An unattributed verdict counts as approvable — the
 *  server, not this function, is the judge of ownership.
 */
export interface DupBatch {
  upload_id: string
  filename: string
  total: number
  theirs: number
}

export function dupBatches(
  pending: RawDupPending[],
  meId: string | null | undefined,
): DupBatch[] {
  const by = new Map<string, DupBatch>()
  for (const v of pending) {
    let b = by.get(v.upload_id)
    if (!b) {
      b = { upload_id: v.upload_id, filename: v.filename, total: 0, theirs: 0 }
      by.set(v.upload_id, b)
    }
    b.total += 1
    if (meId == null || v.resolved_by !== meId) b.theirs += 1
  }
  return [...by.values()]
}

/** Judge every pending verdict on one file at once — the checker's other half
 *  of keep-one, which records hundreds of row verdicts from one decision.
 *  Invalidates `keep-one` too, so the panel's "awaiting a checker" line moves
 *  with the approval instead of going on saying nothing has. */
export function useRawDupBatchReview() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (p: { uploadId: string; decision: 'approve' | 'reject'; comment?: string }) =>
      postJson<{ decision: string; reviewed: number; skipped_own: number; entries_settled: number }>(
        `/uploads/${p.uploadId}/duplicates/review`,
        { decision: p.decision, comment: p.comment ?? null },
      ),
    onSuccess: () => {
      for (const k of ['raw-dup-review', 'upload-rows', 'upload-companies', 'raw-summary',
                       'keep-one', 'grid', 'name-mapping']) {
        void qc.invalidateQueries({ queryKey: [k] })
      }
    },
  })
}

/** The two judgements a held-back keep-one group can receive, as the payloads
 *  the row-grain resolve endpoint takes.
 *
 *  Weak groups are excluded from the keep-one batch because their document
 *  also carries other lines — a person must judge each one. The panel used to
 *  say "judge these with the dup? menu on the rows below", which meant hunting
 *  one document through a 51,912-row table: an instruction where an affordance
 *  belonged. Each group now carries its two possible answers as buttons, and
 *  this is what they post — the SAME verdicts, through the same door, that the
 *  dup? menu records.
 */
export function weakVerdicts(
  g: { keep: number; dispose: number[] },
  decision: 'both_real' | 'keep_one',
): { row_indexes: number[]; verdict: 'genuine' | 'not_duplicate' }[] {
  if (decision === 'both_real') {
    return [{ row_indexes: [g.keep, ...g.dispose], verdict: 'not_duplicate' }]
  }
  return [
    { row_indexes: [g.keep], verdict: 'not_duplicate' },
    { row_indexes: g.dispose, verdict: 'genuine' },
  ]
}

/** Approve or reject one row verdict. Both invalidate the Raw Data queries as
 *  well as the queue: approving makes the row read as judged and rejecting
 *  takes the verdict off it, and both move the outstanding-duplicate counts
 *  the banner and the block gate are reading. */
export function useRawDupDecision() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (p: { id: string; decision: 'approve' | 'reject'; comment?: string }) =>
      postJson(`/raw-duplicate-verdicts/${p.id}/${p.decision}`, {
        comment: p.comment ?? null,
      }),
    onSuccess: () => {
      for (const k of ['raw-dup-review', 'upload-rows', 'upload-companies', 'raw-summary']) {
        void qc.invalidateQueries({ queryKey: [k] })
      }
    },
  })
}
